"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFontSize = exports.useLayout = exports.useMediaQuery = exports.useHover = exports.useScreenSize = void 0;
/* eslint-disable react/display-name */
const react_1 = __importDefault(require("react"));
const react_native_1 = require("react-native");
const convertUnits_1 = require("./convertUnits");
const mediaQueries_1 = require("./cssToRN/mediaQueries");
/** Hook that will apply the screen size to the styles defined with vmin, vmax, vw, vh units, and handle media queries constraints */
const useScreenSize = () => {
    const { width, height } = (0, react_native_1.useWindowDimensions)();
    return react_1.default.useMemo(() => ({
        vw: width / 100, vh: height / 100, vmin: Math.min(width, height) / 100, vmax: Math.max(width, height) / 100
    }), [height, width]);
};
exports.useScreenSize = useScreenSize;
/** Hook that will apply the style reserved for hover state if needed */
const useHover = (onMouseEnter, onMouseLeave, needsHover) => {
    const [hover, setHover] = react_1.default.useState(false);
    const hoverStart = react_1.default.useMemo(() => needsHover ? (event) => {
        if (onMouseEnter)
            onMouseEnter(event);
        setHover(true);
    } : undefined, [needsHover, onMouseEnter]);
    const hoverStop = react_1.default.useMemo(() => needsHover ? (event) => {
        if (onMouseLeave)
            onMouseLeave(event);
        setHover(false);
    } : undefined, [needsHover, onMouseLeave]);
    return { hover, onMouseEnter: hoverStart || onMouseEnter, onMouseLeave: hoverStop || onMouseLeave };
};
exports.useHover = useHover;
/** Hook that will apply the style provided in the media queries */
const useMediaQuery = (media, units) => {
    const mediaStyle = react_1.default.useMemo(() => {
        if (media) {
            const context = (0, mediaQueries_1.createContext)(units);
            const mediaStyles = media.map(m => m(context)).filter(m => !!m);
            if (!mediaStyles.length)
                return;
            const mq = {};
            Object.assign(mq, ...mediaStyles);
            return mq;
        }
    }, [media, units]);
    return mediaStyle;
};
exports.useMediaQuery = useMediaQuery;
/** Hook that will measure the layout to handle styles that use % units */
const useLayout = (onLayout, needsLayout) => {
    const [layout, setLayout] = react_1.default.useState({ width: 0, height: 0 });
    // Prevent calling setState if the component is unmounted
    const unmounted = react_1.default.useRef(false);
    react_1.default.useEffect(() => () => { unmounted.current = true; }, []);
    const updateLayout = react_1.default.useMemo(() => needsLayout ? (event) => {
        if (unmounted.current)
            return;
        if (onLayout)
            onLayout(event);
        const { width, height } = event.nativeEvent.layout;
        setLayout(layout => layout.width === width && layout.height === height ? layout : { width, height });
    } : undefined, [needsLayout, onLayout]);
    return { onLayout: updateLayout || onLayout, ...layout };
};
exports.useLayout = useLayout;
/** Apply the new fontSize to the component before we can calculate em units */
const useFontSize = (fontSizeTarget, rem, em) => {
    const [fontSize, fontUnit] = react_1.default.useMemo(() => fontSizeTarget === undefined ? [] : (0, convertUnits_1.parseValue)(fontSizeTarget), [fontSizeTarget]);
    const isRelative = fontUnit && ['rem', 'em', '%'].includes(fontUnit);
    const newSize = react_1.default.useMemo(() => {
        if (fontSize && isRelative) {
            const newSize = fontUnit === 'em' ? em * fontSize
                : fontUnit === 'rem' ? fontSize * rem
                    : fontUnit === '%' ? em * (1 + fontSize / 100)
                        : fontSize;
            return newSize;
        }
        else
            return fontSize || em;
    }, [em, fontSize, fontUnit, isRelative, rem]);
    return { em: newSize };
};
exports.useFontSize = useFontSize;
